/* ═══════════════════════════════════════════════════
   Step 3 — Final Review & Send (with Wave Orchestration preview + Wallet gating)
   ═══════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FileText, Package, DollarSign, CalendarDays, Radio, Users, BarChart3, Waves, Send, MapPin, Activity, AlertTriangle, Wallet, Check, Layers, Info } from 'lucide-react';
import { T, P, CARD, BORDER } from './constants';
import { formatGeoTarget } from './constants';
import BurstPlanVisualizer from './BurstPlanVisualizer';
import KpiBenchmarkBuilder from './KpiBenchmarkBuilder';
import BriefAuthor, { isBriefValid } from './BriefAuthor';
import DiversityScorePill from './DiversityScorePill';
import ScientificCohortView from '../dynamics/ScientificCohortView';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Step3Review({
  state, selectedCreatorsCount, totalKpiCount, selectedSKUObj,
  onGenerateRfps, burstPlan, burstPlanLoading, burstPlanError,
  onPreviewBurstPlan, canPreview, kpiBenchmarks, onKpiBenchmarksChange,
  brief, onBriefChange, briefClonedFrom, onBriefCloneSet,
}) {
  const { user } = useAuth();
  const { campaignName, budget, startDate, endDate, platforms, geoLevel, selectedStates, selectedDmas, zipCodes, waveType, waveId } = state;
  const geoValue = formatGeoTarget(geoLevel, selectedStates, selectedDmas, zipCodes);
  const items = [
    { l: 'Campaign', v: campaignName, i: FileText },
    { l: 'Product', v: selectedSKUObj?.name || '—', i: Package },
    { l: 'Budget', v: `$${Number(budget).toLocaleString()}`, i: DollarSign },
    { l: 'Flight', v: `${startDate} → ${endDate}`, i: CalendarDays },
    { l: 'Platforms', v: platforms.join(', '), i: Radio },
    { l: 'Geo-Target', v: geoValue, i: MapPin },
    { l: 'Cohort', v: `${selectedCreatorsCount} creators (of 250+)`, i: Users },
    { l: 'KPIs', v: `${totalKpiCount} metrics set`, i: BarChart3 },
    { l: 'Wave Type', v: waveType, i: Waves },
  ];

  // ═══ Wallet gating ═══
  // Get wallet balance from user context for early blocking (before wave creation)
  const userWalletBalance = user?.wallet_balance ?? null;
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!waveId) {
        // Fall back to client-side estimate so user sees fee breakdown even before wave creation
        // Use user's wallet balance from auth context for early blocking
        const b = Number(budget) || 0;
        const gross = +(b * 1.1).toFixed(2);
        const walletSufficient = userWalletBalance !== null ? userWalletBalance >= gross : null;
        const shortfall = userWalletBalance !== null && !walletSufficient ? gross - userWalletBalance : 0;
        setQuote({ 
          budget: b, 
          platform_fee: +(b * 0.1).toFixed(2), 
          platform_fee_rate: 0.1, 
          gross, 
          wallet_balance: userWalletBalance, 
          sufficient: walletSufficient,
          shortfall
        });
        return;
      }
      try {
        const r = await axios.get(`${API}/api/payments/wave/${waveId}/quote`, { withCredentials: true });
        if (!cancelled) setQuote(r.data);
      } catch (err) {
        // Quote is a nice-to-have gate; if it fails we still let the user see
        // the client-side fee estimate above. Don't block the review page.
        if (process.env.NODE_ENV !== 'production') console.warn('wave quote fetch failed:', err?.response?.status || err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [waveId, budget, userWalletBalance]);

  const sufficient = quote?.sufficient;
  // Block if wallet is insufficient - works both before and after wave creation
  const blocked = sufficient === false;
  // Build-Group A: brief is required by the backend; gate the Send button
  // here so the brand sees the inline validation BEFORE wasting an API call.
  const briefOk = isBriefValid(brief);

  return (
    <div className="space-y-5" data-testid="step-review">
      <h2 className="text-lg font-bold text-white">Final Review</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {items.map(item => (
          <div key={item.l} className="rounded-xl p-4" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
            <div className="flex items-center gap-2 mb-1.5">
              <item.i className="h-3.5 w-3.5" style={{ color: T }} />
              <span className="text-[9px] text-gray-600 uppercase font-semibold">{item.l}</span>
            </div>
            <p className="text-xs font-semibold text-white">{item.v}</p>
          </div>
        ))}
      </div>

      {/* ═══ Wallet / total breakdown ═══ */}
      {quote && (
        <div className="rounded-xl p-4 grid sm:grid-cols-4 gap-3" data-testid="wave-fee-breakdown"
             style={{ background: CARD, border: `1px solid ${blocked ? '#FF4D94' : BORDER}` }}>
          <FeeCell
            label="Per Creator"
            value={selectedCreatorsCount > 0
              ? `$${(Number(quote.budget) / selectedCreatorsCount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
              : '—'}
            sublabel={selectedCreatorsCount > 0 ? `× ${selectedCreatorsCount} selected` : 'select creators first'}
            color={T}
            testId="fee-per-creator"
          />
          <FeeCell label="Wave Budget" value={`$${Number(quote.budget).toLocaleString()}`} color={T} testId="fee-wave-budget" />
          <FeeCell label="Total Debit" value={`$${Number(quote.gross).toLocaleString()}`} color="#fff" emphasise testId="fee-total-debit" />
          <FeeCell label="Wallet After" value={quote.wallet_balance == null ? '—' : `$${Math.max(0, Number(quote.wallet_balance) - Number(quote.gross)).toLocaleString()}`} color={blocked ? '#FF4D94' : '#a1a1aa'} testId="fee-wallet-after" />
        </div>
      )}

      {blocked && (
        <div className="rounded-xl p-4 flex items-center gap-3" data-testid="insufficient-funds-banner"
             style={{ background: 'rgba(255,77,148,0.1)', border: '1px solid rgba(255,77,148,0.3)' }}>
          <AlertTriangle className="h-5 w-5 text-[#FF4D94] flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-bold text-[#FF4D94]">Insufficient funds — Top up wallet to activate</p>
            <p className="text-[11px] text-gray-400 mt-0.5">You need ${Number(quote.shortfall).toLocaleString()} more to cover this Wave.</p>
          </div>
          <Link to="/billing" data-testid="wallet-topup-from-wave-btn"
                className="px-4 py-2.5 rounded-lg bg-[#FF4D94] text-white font-bold text-xs hover:brightness-110 hover:scale-[1.02] transition-all flex items-center gap-1.5"
                style={{ boxShadow: '0 0 16px rgba(255,77,148,0.35)' }}>
            <Wallet className="h-3.5 w-3.5" /> Top up wallet
          </Link>
        </div>
      )}

      {sufficient === true && (
        <div className="rounded-xl p-3 flex items-center gap-2.5" data-testid="funds-ok-banner"
             style={{ background: 'rgba(74,222,128,0.05)', border: '1px solid rgba(74,222,128,0.2)' }}>
          <Check className="h-4 w-4 text-green-400" />
          <p className="text-[11px] text-green-400 font-semibold">Wallet covers this Wave. Fee disclosed — ready to launch.</p>
        </div>
      )}

      {/* iter 78 — Footprint preview: if the brand has uploaded any footprint
          data, surface it here BEFORE they click Generate RFPs so they know
          the captions will be geo-aware from the start. */}
      <FootprintPreviewCard />

      {/* ═══ Build-Group A — structured Creative Brief ═══ */}
      <BriefAuthor
        brief={brief}
        onChange={onBriefChange}
        clonedFrom={briefClonedFrom}
        onCloneCleared={onBriefCloneSet}
      />

      {/* ═══ KPI Benchmarks — ships with the RFP + measured live ═══ */}
      <KpiBenchmarkBuilder
        waveId={state.waveId}
        initial={kpiBenchmarks || []}
        onChange={onKpiBenchmarksChange}
        goal={state.campaignGoal}
        skuId={state.selectedSku}
        platforms={state.platforms}
      />

      {/* ═══ Orchestration preview CTA ═══ */}
      {!burstPlan && !burstPlanLoading && (
        <div className="rounded-xl p-5 relative overflow-hidden"
             style={{
               background: `linear-gradient(135deg, ${CARD}, #0f0c1c)`,
               border: `1px solid ${BORDER}`,
             }}>
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20 blur-3xl"
               style={{ background: `radial-gradient(circle, ${P}, transparent)` }} />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div className="max-w-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-4 w-4" style={{ color: T }} />
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">See the wave before it goes live</span>
              </div>
              <p className="text-sm text-white font-semibold leading-snug mb-1">
                Preview the algorithmic orchestration plan
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Our Wave Orchestrator engineers the precise burst timing, patient-zero seeding, per-platform stagger,
                content-angle diversity, and predicted velocity curve — tuned to replicate organic trend formation.
              </p>
            </div>
            <button
              onClick={onPreviewBurstPlan}
              disabled={!canPreview}
              data-testid="preview-burst-plan-btn"
              className="px-6 py-3 rounded-xl text-xs font-bold text-white flex items-center gap-2 disabled:opacity-40 transition-all hover:scale-[1.02]"
              style={{ background: `linear-gradient(135deg, ${T}, ${P})`, color: '#000', boxShadow: `0 0 30px ${P}40` }}
            >
              <Activity className="h-4 w-4" />
              Preview Burst Plan
            </button>
          </div>
          {!canPreview && (
            <p className="mt-2 text-[10px] text-amber-500/80">
              Generate RFPs first (button below) to unlock the burst preview.
            </p>
          )}
        </div>
      )}

      {/* Orchestration visualization (appears once preview is fetched) */}
      {(burstPlan || burstPlanLoading || burstPlanError) && (
        <BurstPlanVisualizer
          plan={burstPlan}
          loading={burstPlanLoading}
          error={burstPlanError}
          onRefresh={onPreviewBurstPlan}
          waveId={state.waveId}
          waveStatus={state.waveStatus}
        />
      )}

      {/* ═══ Cohort Manifold Dynamics — Invention 9 ═══
          Lives under the burst plan so it reads as a science layer on top
          of the orchestrator (which it literally is). Collapsed by default
          — brand managers who don't care about the science can ignore it
          entirely and the page reads identically to pre-CMD. */}
      <ScientificCohortView waveId={state.waveId} />

      {/* iter 61: don't leave the brand staring at a grey button with no hint.
          Surface the exact blocker above the CTA so they can fix it. */}
      {(selectedCreatorsCount === 0 || blocked || !briefOk) && (
        <div className="rounded-lg px-4 py-3 flex items-start gap-3 mt-3"
             style={{ background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.35)' }}
             data-testid="send-blocker">
          <div className="text-[#FF6B6B] text-base flex-shrink-0">!</div>
          <div className="flex-1">
            <p className="text-[11px] font-black uppercase tracking-wider text-[#FF6B6B]">
              {selectedCreatorsCount === 0
                ? 'No creators selected'
                : !briefOk
                  ? 'Creative brief incomplete'
                  : 'Wallet balance too low'}
            </p>
            <p className="text-[11px] text-gray-300 mt-1 leading-relaxed">
              {selectedCreatorsCount === 0
                ? 'Go back to Step 2 and select at least one creator from the cohort before sending.'
                : !briefOk
                  ? 'Fill in the three brief fields above — format, 2 to 3 message points, and creative freedom.'
                  : `This wave's total cost exceeds your current wallet. Top up to proceed — your cohort + RFP are saved as a draft.`}
            </p>
            {blocked && briefOk && (
              <Link to="/billing" className="inline-block mt-2 text-[10px] font-black uppercase tracking-wider text-[#00D4FF] hover:brightness-110" data-testid="topup-link">
                Top up wallet →
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-center pt-2">
        <button onClick={onGenerateRfps} disabled={selectedCreatorsCount === 0 || blocked || !briefOk}
                className="px-10 py-4 rounded-xl text-sm font-bold text-white flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: P, boxShadow: `0 0 20px ${P}30` }} data-testid="generate-rfps-btn">
          <Send className="h-4 w-4" />
          {blocked
            ? 'Top up wallet to send'
            : selectedCreatorsCount === 0
              ? 'Select creators first'
              : !briefOk
                ? 'Complete the brief first'
                : `Generate RFP Drafts (Preview Next) — $0.00 charged`}
        </button>
      </div>
    </div>
  );
}

function FeeCell({ label, value, sublabel, color, emphasise, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">{label}</p>
      <p className={`mt-1 font-black ${emphasise ? 'text-xl' : 'text-base'}`} style={{ color: color || '#fff' }}>{value}</p>
      {sublabel && (
        <p className="text-[10px] text-gray-500 mt-0.5">{sublabel}</p>
      )}
    </div>
  );
}


// ── 1st Party Data Room Preview Card ─────────────────────────────────
//  Reads GET /api/brand/data-room/context — if any uploads exist, shows
//  what brand context the LLM will inject into every caption. Brands
//  without data see a soft prompt to upload.
function FootprintPreviewCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/brand/data-room/context`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData({ has_data: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  if (!data.has_data) {
    return (
      <div
        className="rounded-xl p-3 flex items-start gap-2.5"
        style={{ background: 'rgba(167,139,250,0.04)', border: '1px dashed rgba(167,139,250,0.25)' }}
        data-testid="footprint-preview-empty"
      >
        <MapPin className="h-4 w-4 text-[#A78BFA] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold text-white mb-0.5">Captions will be generic</p>
          <p className="text-[10px] text-gray-400 leading-snug">
            Upload your customer data in the{' '}
            <Link to="/data-room" className="text-[#A78BFA] hover:underline font-semibold">1st Party Data Room</Link>{' '}
            and every caption will be personalised to your brand.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-3.5"
      style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.08), rgba(74,222,128,0.04))', border: '1px solid rgba(0,212,255,0.25)' }}
      data-testid="footprint-preview-active"
    >
      <div className="flex items-start gap-2.5">
        <MapPin className="h-4 w-4 text-[#00D4FF] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-black text-[#00D4FF] mb-1">
            Drawing from your footprint
          </p>
          <p className="text-[11px] text-white leading-snug mb-2">
            {data.prose_summary}
          </p>
          {data.top_states?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {data.top_states.map(s => (
                <span
                  key={s.abbr}
                  className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(0,212,255,0.12)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}
                >
                  {s.name} · {s.rows.toLocaleString()}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
